import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { PlusOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { Upload } from 'antd';
import MobileDatePicker from '../common/MobileDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useAppSelector } from '../../hooks/redux';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { toIsoDate } from '../../utils/dateHelpers';
import {
  canManageFinanzas,
  canPayExpenseFromCash,
  canSeeAllExpenses,
  isSupportUser,
} from '../../utils/roleHelpers';
import { compressImage, HEIC_HINT } from '../../utils/imageCompression';
import {
  useCreateExpenseMutation,
  useGetExpensePaymentMethodsQuery,
  useUploadExpenseAttachmentMutation,
} from '../../services/expensesApi';
import {
  useGetActiveCashRegistersQuery,
  useGetAllOpenCashRegistersQuery,
} from '../../services/cashRegisterApi';
import {
  ExpenseCategoryDto,
  CreateExpenseRequest,
  ExpenseCaptureResultDto,
  SuggestedField,
} from '../../types/expense';
import QuickAddPaymentMethodModal from './QuickAddPaymentMethodModal';
import SuggestedFieldMark from './SuggestedFieldMark';

const { Text } = Typography;

interface Props {
  open: boolean;
  restaurantId: string;
  categories: ExpenseCategoryDto[];
  /**
   * Resultado de la autocaptura, si el gasto viene de una foto. Precarga los campos y
   * —lo más importante— lleva el `captureId`: sin mandarlo, el gasto se guardaría sin su
   * comprobante adjunto y la captura quedaría colgada hasta vencer.
   */
  capture?: ExpenseCaptureResultDto | null;
  onClose: () => void;
}

interface FormValues {
  expenseDate: Dayjs;
  categoryId: string;
  amount: number;
  vendor?: string;
  notes?: string;
  cashRegisterId?: string;
  paymentMethodId?: string;
}

const ExpenseFormDialog: React.FC<Props> = ({
  open,
  restaurantId,
  categories,
  capture,
  onClose,
}) => {
  const [form] = Form.useForm<FormValues>();
  const { user } = useAppSelector((state) => state.auth);
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  // AntD v5: use App.useApp() for message context — see ExpenseCancelDialog comment.
  const { message } = App.useApp();
  // Chip toggle (no Form.Item): el payload lo toma de este estado
  const [paidFromCash, setPaidFromCash] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [createExpense] = useCreateExpenseMutation();
  const [uploadAttachment] = useUploadExpenseAttachmentMutation();
  const [attachments, setAttachments] = useState<UploadFile[]>([]);

  /**
   * isSubmitting propio y NO el isLoading de createExpense.
   *
   * El isLoading de la mutation vuelve a false en cuanto el gasto se creó, mientras
   * los uploads siguen corriendo. Con el botón atado a isLoading se re-habilita a
   * media subida y un segundo clic crea un gasto DUPLICADO — el mismo patrón que ya
   * nos mordió en cash_movement_double_click, un paso más adelante en el flujo.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * El Gerente no puede pagar desde caja ni administrar métodos de pago.
   *
   * PaidFromCash inserta un CashMovement que reduce el efectivo esperado de la caja
   * de un cajero — o sea, permitiría cubrir un faltante, que es justo el fraude que
   * los comprobantes buscan prevenir. Ver docs/finanzas-module.md, "Gerente y caja".
   *
   * Esconder el control NO es la barrera: el backend rechaza con 403. Esto evita
   * que el usuario se tope con el error, y que el form dispare un 403 al montar el
   * query de cajas abiertas (all-open sigue cerrado a Gerente).
   */
  const canManage = canManageFinanzas(user?.role, isSupportUser(user?.email));

  /**
   * ¿Ve todas las cajas, o sólo la suya?
   *
   * El personal de piso y caja SÍ puede pagar desde caja — pero únicamente desde la
   * propia, y el backend lo verifica bajo el candado de la fila
   * (ExpensesService, `ownCashRegisterOnly`). Aquí eso se traduce en dos endpoints
   * distintos: `all-open` (cerrado a Administrador) contra `active`, que devuelve la
   * caja del propio llamante. Pedir `all-open` con un rol operativo daría 403 y dejaría
   * el selector vacío sin explicación.
   */
  const seesAllRegisters = canSeeAllExpenses(user?.role, isSupportUser(user?.email));

  /**
   * ¿Puede pagar desde caja? Es un eje distinto de "puede capturar": Mesero puro y Chef
   * registran gastos pero no mueven caja, igual que no cobran. Ver roleHelpers.
   */
  const canPayFromCash = canPayExpenseFromCash(user?.role, isSupportUser(user?.email));

  // Only fetch open registers when the toggle is on (avoids a query for the common case).
  // El skip por canManage es necesario, no cosmético: sin él un Gerente dispararía un
  // 403 contra /cash-registers/all-open.
  const { data: allOpenRegisters, isFetching: loadingAllRegisters } = useGetAllOpenCashRegistersQuery(
    undefined,
    { skip: !open || !paidFromCash || !canManage }
  );

  // La caja del propio usuario. Devuelve null cuando no tiene ninguna abierta.
  const { data: ownActiveRegister, isFetching: loadingOwnRegister } = useGetActiveCashRegistersQuery(
    undefined,
    { skip: !open || !paidFromCash || seesAllRegisters }
  );

  // Una sola lista aguas abajo: el resto del form no tiene por qué saber de dónde salió.
  //
  // `undefined` significa "todavía no sé" y NO se colapsa a lista vacía: abajo,
  // hasNoOpenRegisters distingue las dos cosas, y confundirlas pinta "No hay cajas
  // abiertas" durante el parpadeo de la carga — un error rojo sobre un dato que aún no
  // llegó. La query de la caja propia devuelve null (no undefined) cuando de verdad no
  // hay ninguna, así que las dos situaciones son distinguibles.
  const openRegisters = useMemo(() => {
    if (seesAllRegisters) return allOpenRegisters;
    if (ownActiveRegister === undefined) return undefined;
    return ownActiveRegister ? [ownActiveRegister] : [];
  }, [seesAllRegisters, allOpenRegisters, ownActiveRegister]);
  const loadingRegisters = seesAllRegisters ? loadingAllRegisters : loadingOwnRegister;

  // Active-only methods for the dropdown — soft-deleted entries shouldn't pollute new
  // captures. The Quick-add modal queries with includeInactive=true separately for dedup.
  const { data: paymentMethods, isFetching: loadingPaymentMethods } =
    useGetExpensePaymentMethodsQuery({ restaurantId }, { skip: !open });

  // Preselect the register if the user owns the only open one.
  useEffect(() => {
    if (!paidFromCash || !openRegisters) return;
    if (openRegisters.length === 0) return;

    const ownRegister = user ? openRegisters.find((r) => r.userId === user.id) : undefined;
    const onlyOwn = ownRegister && openRegisters.length === 1 ? ownRegister : undefined;

    if (onlyOwn) {
      form.setFieldsValue({ cashRegisterId: onlyOwn.id });
    }
  }, [paidFromCash, openRegisters, user, form]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ expenseDate: dayjs() });
      setPaidFromCash(false);
      setAttachments([]);
      setEditedFields(new Set());

      // Precarga desde la foto. Sólo los campos que el sistema logró leer: un campo
      // ausente es una respuesta legítima y se deja vacío, no en cero ni en "hoy".
      const suggestion = capture?.suggestion;
      if (suggestion) {
        form.setFieldsValue({
          ...(suggestion.amount ? { amount: suggestion.amount.value } : {}),
          ...(suggestion.expenseDate ? { expenseDate: dayjs(suggestion.expenseDate.value) } : {}),
          ...(suggestion.vendor ? { vendor: suggestion.vendor.value } : {}),
          ...(suggestion.categoryId ? { categoryId: suggestion.categoryId.value } : {}),
        });
      }
    }
  }, [open, form, capture]);

  /**
   * Campos que el usuario ya tocó: su marca desaparece.
   *
   * Sin quitarla, el marcado deja de significar "esto lo leyó la máquina, revísalo" y
   * pasa a ser decorado permanente — y un aviso que siempre está es un aviso que nadie
   * lee, que es justo lo que convierte la autocaptura en riesgo en vez de ayuda.
   */
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  const markEdited = (field: string) =>
    setEditedFields((previous) => {
      if (previous.has(field)) return previous;
      const next = new Set(previous);
      next.add(field);
      return next;
    });

  /**
   * Devuelve `undefined` —y no un elemento que renderiza null— cuando no hay nada que
   * marcar.
   *
   * AntD hace `{extra && <div className="ant-form-item-extra">…</div>}`, y un elemento de
   * React es truthy aunque su render devuelva null: pasarle siempre el componente añadiría
   * un div vacío con su padding a los cuatro campos, en TODAS las altas manuales. Un
   * usuario que nunca toca la autocaptura no debería notar que existe.
   */
  const markFor = <T,>(
    field: SuggestedField<T> | null | undefined,
    name: string,
    format?: (value: T) => string,
  ) =>
    field && !editedFields.has(name)
      ? <SuggestedFieldMark field={field} edited={false} format={format} />
      : undefined;

  const hasNoOpenRegisters = paidFromCash && openRegisters !== undefined && openRegisters.length === 0;

  const cashRegisterOptions = useMemo(
    () =>
      (openRegisters || []).map((r) => ({
        value: r.id,
        label: `Caja de ${r.userName} (abierta ${formatInTimeZone(r.openTime, 'HH:mm')})`,
      })),
    [openRegisters, formatInTimeZone]
  );

  const paymentMethodOptions = useMemo(
    () => (paymentMethods || []).map((p) => ({ value: p.id, label: p.name })),
    [paymentMethods]
  );

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // antd already highlights invalid fields
    }

    const payload: CreateExpenseRequest = {
      categoryId: values.categoryId,
      amount: values.amount,
      // Send date-only (yyyy-MM-dd) so the backend interprets it in the restaurant's
      // timezone, NOT the browser's. The old startOf('day').toISOString() shifted by
      // the browser's UTC offset and made expenses land in the wrong P&L bucket for
      // anyone whose browser tz != restaurant tz (Codex review on PR-54).
      expenseDate: toIsoDate(values.expenseDate),
      vendor: values.vendor?.trim() || null,
      notes: values.notes?.trim() || null,
      paidFromCash,
      cashRegisterId: paidFromCash ? values.cashRegisterId : null,
      paymentMethodId: values.paymentMethodId || null,
      // Consume la captura: promueve la foto a comprobante del gasto y libera su cuota.
      // Mandarlo también hace idempotente el alta — un segundo clic devuelve el mismo
      // gasto en vez de crear otro.
      captureId: capture?.captureId ?? null,
    };

    setIsSubmitting(true);
    try {
      const created = await createExpense({ restaurantId, data: payload }).unwrap();

      // Los comprobantes se suben DESPUÉS de crear el gasto, no en el mismo POST.
      // El alta conserva así toda su lógica transaccional de caja intacta (FOR UPDATE
      // sobre CashRegister + CashMovement); meter multipart ahí obligaría a reescribir
      // el path más delicado del módulo para una feature opcional.
      const failures = await uploadAttachments(created.id);

      if (failures === 0) {
        message.success('Gasto registrado');
      } else {
        // El gasto NO se revierte. La captura es lo caro para el empleado; la foto se
        // puede reintentar desde Editar. Fingir éxito o perder el gasto son las dos
        // peores salidas, sobre todo con la señal mala de una tienda.
        message.warning(
          `El gasto se guardó, pero ${failures} comprobante(s) no se pudieron subir. ` +
            'Agrégalos desde Editar.'
        );
      }
      onClose();
    } catch (err: any) {
      // 400/404/409 from controller all surface { message }. Show inline.
      const serverMessage = err?.data?.message;
      message.error(serverMessage || 'No se pudo guardar el gasto. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Sube los adjuntos en serie. Devuelve cuántos fallaron. */
  const uploadAttachments = async (expenseId: string): Promise<number> => {
    let failures = 0;

    for (const item of attachments) {
      const raw = item.originFileObj as File | undefined;
      if (!raw) continue;

      // Compresión en el cliente antes de subir: por datos móviles y porque el
      // backend no decodifica HEIC. Ver utils/imageCompression.ts — el fallback
      // importa, "el canvas lo convierte" no es cierto en Windows/Android.
      const compressed = await compressImage(raw);
      if (!compressed.ok) {
        message.error(`${raw.name}: ${HEIC_HINT}`);
        failures += 1;
        continue; // un HEIC no aborta el resto del lote
      }

      try {
        await uploadAttachment({ restaurantId, expenseId, file: compressed.file }).unwrap();
      } catch (err: any) {
        message.error(err?.data?.message || `No se pudo subir ${raw.name}.`);
        failures += 1;
      }
    }

    return failures;
  };

  return (
    <Modal
      title="Nuevo gasto"
      open={open}
      onCancel={onClose}
      // El modal no se puede cerrar a media subida: cerrarlo dejaría los uploads
      // huérfanos y al usuario sin saber si su comprobante llegó.
      maskClosable={!isSubmitting}
      closable={!isSubmitting}
      footer={
        // En móvil el pie se apila y los botones van a lo ancho: con la nota de
        // capturista en la misma línea, "Guardar gasto" quedaba comprimido en una
        // esquina de la pantalla en la que más se usa este modal.
        <div className="fin-modal-footer">
          {/* Quién queda como capturista — hoy solo se descubre en la tabla */}
          <Text type="secondary" className="fin-modal-footer__lead fin-modal-footer__note">
            {user?.firstName ? `se registrará a nombre de ${user.firstName} ${user.lastName || ''}`.trim() : ''}
          </Text>
          <Button onClick={onClose} disabled={isSubmitting} block={isMobile}>
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || hasNoOpenRegisters}
            block={isMobile}
          >
            Guardar gasto
          </Button>
        </div>
      }
      destroyOnClose
      // Sin ancho especial en móvil: App.css lleva el modal a pantalla completa en
      // ≤768px con !important, así que un 95vw sólo lograría descentrarlo (el margin: 0
      // global le quita el auto). Ver el comentario en finanzas.css.
      width={520}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            {/* El monto es el dato por el que se abre el modal: primero y en grande */}
            <Form.Item
              name="amount"
              label="Monto"
              extra={markFor(capture?.suggestion?.amount, 'amount', (v) => `$${Number(v).toFixed(2)}`)}
              rules={[
                { required: true, message: 'El monto es requerido' },
                {
                  validator: (_r, v) =>
                    v > 0 ? Promise.resolve() : Promise.reject(new Error('El monto debe ser mayor a cero')),
                },
              ]}
            >
              <InputNumber
                style={{
                  width: '100%',
                  fontFamily: 'ui-monospace, Consolas, monospace',
                  fontSize: 17,
                  fontWeight: 650,
                }}
                min={0.01}
                step={1}
                precision={2}
                prefix="$"
                autoFocus
                onChange={() => markEdited('amount')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="expenseDate"
              label="Fecha"
              extra={markFor(capture?.suggestion?.expenseDate, 'expenseDate')}
              rules={[{ required: true, message: 'La fecha es requerida' }]}
            >
              <MobileDatePicker
                format="DD/MM/YYYY"
                allowClear={false}
                onChange={() => markEdited('expenseDate')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="categoryId"
              label="Categoría"
              extra={markFor(
                capture?.suggestion?.categoryId,
                'categoryId',
                (id) => categories.find((c) => c.id === id)?.name ?? String(id),
              )}
              rules={[{ required: true, message: 'La categoría es requerida' }]}
            >
              <Select
                placeholder="Selecciona una categoría"
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                onChange={() => markEdited('categoryId')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="vendor"
              label="Proveedor (opcional)"
              extra={markFor(capture?.suggestion?.vendor, 'vendor')}
            >
              <Input
                maxLength={100}
                placeholder="Ej. Carnicería La Esperanza"
                onChange={() => markEdited('vendor')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12} align="bottom">
          <Col xs={24} sm={12}>
            <Form.Item name="paymentMethodId" label="Método de pago (opcional)" style={{ marginBottom: 4 }}>
              <Select
                placeholder="Selecciona un método"
                allowClear
                options={paymentMethodOptions}
                loading={loadingPaymentMethods}
              />
            </Form.Item>
            {/* Punto 2 de 7 del barrido: el POST del catálogo está cerrado a Gerente. */}
            {canManage && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setQuickAddOpen(true)}
                style={{ paddingLeft: 0, marginBottom: 8 }}
              >
                Agregar nuevo método
              </Button>
            )}
          </Col>
          {/* Punto 1 de 7: el toggle se OCULTA para Gerente, no se deshabilita.
              El personal de caja sí lo ve — paga desde la suya, ver canPayFromCash. */}
          {canPayFromCash && (
            <Col xs={24} sm={12} style={{ paddingBottom: 12 }}>
              <button
                type="button"
                className={`fin-pill${paidFromCash ? ' fin-pill--selected' : ''}`}
                onClick={() => setPaidFromCash(!paidFromCash)}
              >
                💵 Pagado desde caja
              </button>
            </Col>
          )}
        </Row>

        {paidFromCash && (
          <div className="fin-cashblock">
            {hasNoOpenRegisters ? (
              <Alert
                type="error"
                showIcon
                message={seesAllRegisters ? 'No hay cajas abiertas.' : 'No tienes una caja abierta.'}
                description={
                  seesAllRegisters
                    ? "Abre una caja antes de registrar el gasto, o desactiva 'Pagado desde caja'."
                    : "Sólo puedes pagar desde tu propia caja. Ábrela primero, o desactiva 'Pagado desde caja' y registra el gasto sin afectarla."
                }
                style={{ marginBottom: 10 }}
              />
            ) : (
              <Form.Item
                name="cashRegisterId"
                label="Caja"
                rules={[
                  {
                    required: true,
                    message: 'Selecciona la caja desde la que se pagó',
                  },
                ]}
                extra={
                  loadingRegisters ? (
                    <Text type="secondary">Cargando cajas abiertas…</Text>
                  ) : null
                }
              >
                <Select
                  placeholder="Selecciona una caja"
                  options={cashRegisterOptions}
                  loading={loadingRegisters}
                />
              </Form.Item>
            )}
          </div>
        )}

        <Form.Item name="notes" label="Notas (opcional)">
          <Input.TextArea maxLength={500} rows={2} showCount />
        </Form.Item>

        <Form.Item label="Comprobantes (opcional)" style={{ marginBottom: 0 }}>
          <Upload
            listType="picture-card"
            multiple
            maxCount={5}
            // Sin capture="environment" a propósito: en iOS el picker nativo ya ofrece
            // "Tomar foto", y forzar la cámara bloquearía elegir el PDF de una factura.
            accept="image/*,application/pdf"
            fileList={attachments}
            onChange={({ fileList }) => setAttachments(fileList)}
            // beforeUpload=false acumula en estado local: los archivos se suben
            // después de crear el gasto, contra su id.
            beforeUpload={() => false}
            disabled={isSubmitting}
          >
            {attachments.length >= 5 ? null : (
              <div>
                <PaperClipOutlined />
                <div style={{ marginTop: 6, fontSize: 12 }}>Adjuntar</div>
              </div>
            )}
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Foto del ticket o PDF de la factura. Máximo 5.
          </Text>
        </Form.Item>
      </Form>

      <QuickAddPaymentMethodModal
        open={quickAddOpen}
        restaurantId={restaurantId}
        onClose={() => setQuickAddOpen(false)}
        onCreated={(created) => {
          // Auto-select the freshly created method in this form's dropdown. This is the
          // pivot of the inline-add UX — without setFieldsValue, the user would lose what
          // they just created and have to re-pick it from the dropdown after the cache
          // refetch lands. setFieldsValue runs synchronously against the still-mounted
          // form (QuickAdd's onClose fires after this callback by contract).
          form.setFieldsValue({ paymentMethodId: created.id });
        }}
      />
    </Modal>
  );
};

export default ExpenseFormDialog;
